import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Save, X } from 'lucide-react';
import StepIndicator from '@/components/wizard/StepIndicator';
import ProjectTypeStep from '@/components/wizard/steps/ProjectTypeStep';
import BasicInfoStep from '@/components/wizard/steps/BasicInfoStep';
import SheetIndexStep from '@/components/wizard/steps/SheetIndexStep';
import DocumentPreview from '@/components/preview/DocumentPreview';
import api from '@/utils/api';
import { toast } from 'sonner';

const ProjectWizard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [projectData, setProjectData] = useState({
    name: '',
    project_type: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    client_name: '',
    architect_name: '',
    scope_of_work: '',
    building_summary: {},
    consultants: [],
    sheets: [],
    door_schedule: [],
    window_schedule: [],
    finish_schedule: [],
    renders: [],
  });
  
  const steps = [
    { id: 0, title: t('projectType'), component: ProjectTypeStep },
    { id: 1, title: t('basicInfo'), component: BasicInfoStep },
    { id: 2, title: t('sheetIndex'), component: SheetIndexStep },
  ];
  
  const CurrentStepComponent = steps[currentStep].component;
  
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        // Must select project type
        if (!projectData.project_type) {
          toast.error(t('pleaseSelectType'));
          return false;
        }
        return true;
      case 1:
        // Must have project name
        if (!projectData.name || projectData.name.trim() === '') {
          toast.error('Please enter a project name');
          return false;
        }
        return true;
      default:
        return true;
    }
  };
  
  const handleNext = () => {
    if (!canProceed()) return;
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSave = async () => {
    try {
      // Validate minimum required data
      if (!projectData.project_type) {
        toast.error(t('pleaseSelectType'));
        return;
      }
      
      if (!projectData.name || projectData.name.trim() === '') {
        toast.error('Please enter a project name');
        return;
      }
      
      const project = await api.createProject({
        name: projectData.name,
        project_type: projectData.project_type,
        address: projectData.address,
        city: projectData.city,
        state: projectData.state,
      });
      
      // Update with additional data
      await api.updateProject(project.id, projectData);
      
      toast.success(t('projectCreated'));
      navigate('/');
    } catch (error) {
      toast.error(t('errorCreating'));
      console.error('Error:', error);
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {t('createNewProject')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {t('followSteps')}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          data-testid="cancel-wizard-btn"
        >
          <X className="mr-2 h-4 w-4" />
          {t('cancel')}
        </Button>
      </div>
      
      {/* Step Indicator */}
      <StepIndicator steps={steps} currentStep={currentStep} />
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <Card className="p-8 border border-slate-200 dark:border-slate-700 bg-white">
            <CurrentStepComponent
              data={projectData}
              onChange={setProjectData}
            />
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                data-testid="prev-btn"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                {t('previous')}
              </Button>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handleSave}
                  data-testid="save-draft-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t('save')}
                </Button>
                
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                    data-testid="next-btn"
                  >
                    {t('next')}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    className="bg-[#2d3748] hover:bg-[#1f2937] text-white"
                    data-testid="finish-btn"
                  >
                    {t('finish')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
        
        {/* Preview Section */}
        <div className="lg:col-span-1">
          <DocumentPreview projectData={projectData} />
        </div>
      </div>
    </div>
  );
};

export default ProjectWizard;